<!-- eslint-disable vue/no-v-html -->
<template>
  <div ref="container" class="stream-content" @scroll="onScroll">
    <div class="metadata">
      <div v-if="sessionContext.invention" class="info">Invention: [ {{ sessionContext.invention.references.join(';') }} ]</div>
      <div v-if="sessionContext.document" class="info">Document: {{ sessionContext.document.name }}</div>
      <div v-if="sessionContext.publicData" class="warning">
        Public Data flag is set
      </div>
    </div>
    <div v-if="currentRun.response.length">
      <div v-for="(message, index) in currentRun.response" :key="message.key">
        <div v-if="message.type === 'form'" class="form">
          <div class="form-body">
            <span>
              <label class="prefix">{{ linkPrefixText(message) }}</label
              ><label> {{ message.name }}</label></span
            >
            <div v-if="message.isResolvingDocCodes" class="resolving-docs">
              <MdIcon name="progress-clock" size="sm" class="spin" />
              Resolving document sources...
            </div>
            <div class="variables">
              <FormBody
                :item="getLinkFromMessage(message)"
                :context="currentContext"
                :read-only="message.done || !isRequestPending || message.isResolvingDocCodes"
                :values-only="true"
                :suggestions="[]"
                @change="e => onFormChanged(e, message)"
              />
            </div>
          </div>
          <Button
            v-if="!message.done && isRequestPending"
            class="continue-button"
            :disabled="!isFormRequiredFieldsProvided(message) || message.isResolvingDocCodes"
            @click="next(message)"
            @keyup.enter="next(message)"
          >
            <MdIcon
              title="Continue"
              name="arrow-down-bold"
              size="sm"
              :class="{ 'disabled-icon': !isFormRequiredFieldsProvided(message) }"
            />continue</Button
          >
        </div>
        <div v-else-if="message.type === 'tool' && message.callId" class="toolOrThinking">
          <div class="header">
            <Button color="secondary" class="button" @click="toggleToll(message)">
              <div class="label">
                <MdIcon name="function-variant" size="sm" />
                <span class="label">{{ message.author }}</span>

                <MdIcon :name="message.expanded ? 'chevron-up' : 'chevron-down'" size="sm" />
              </div>
            </Button>
            <template v-if="!message.result">
              <div class="typing">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </template>
          </div>
          <div v-if="message.text" class="body" :class="{ expanded: message.expanded }">
            <div>
              <span> ARGUMENTS:</span>
              <div v-html="renderHtmlAsText(message.arguments)"></div>
            </div>
            <div><span> PRESERVE IN CONVERSATION:</span> {{ !!message.cache }}</div>
            <div>
              <span> RESULT: </span><br />
              <JsonView v-if="isResultObject(message.text)" :value="getResult(message.text)" class="json" />
              <div v-else v-html="renderHtmlAsText(message.text)"></div>
            </div>
          </div>
        </div>
        <div v-else-if="message.type === 'thinking'" class="toolOrThinking">
          <div class="header">
            <Button color="primary" class="button" @click="toggleToll(message)">
              <div class="label">
                <span>💡</span>
                <span class="label">{{ message.author }}</span>

                <MdIcon :name="message.expanded ? 'chevron-up' : 'chevron-down'" size="sm" />
              </div>
            </Button>
          </div>
          <div v-if="message.text" class="body" :class="{ expanded: message.expanded }">
            <div v-html="renderHtmlAsText(message.text)"></div>
          </div>
        </div>
        <div v-else :class="message.author === 'CURRENT_USER' ? 'user' : 'model'" class="message">
          <div class="message-header">
            <span class="label" :title="message.system">
              <MdIcon v-if="message.author !== 'CURRENT_USER'" :name="responseType(message)" size="sm" />
              {{ message.author === 'CURRENT_USER' ? email : message.author }}
              <MdIcon v-if="message.retry && !message.error" title="retry" name="alert-circle-outline" color="warning" size="sm" />
              <MdIcon v-if="message.error" title="error" name="alert-circle-outline" color="error" size="sm" />
              <Button
                v-if="message.request && message.author === 'CURRENT_USER'"
                color="secondary"
                class="template-data-button"
                title="View template data"
                @click="toggleTemplateData(message)"
              >
                <MdIcon name="code-braces" size="sm" />
              </Button>
              <template v-if="isRequestPending && index === currentRun.response.length - 1">
                <div class="typing">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </template>
            </span>
          </div>
          <div v-if="message.files" class="files">
            <div v-for="file in message.files" :key="file.name">
              <div class="file">
                <div v-if="['png', 'svg', 'jpeg', 'jpg'].includes(getFileType(file))" class="thumbnail">
                  {{ file.name }}
                  <img :src="file.base64" :alt="file.name" />
                </div>
                <div v-else-if="getFileType(file) === 'docx'" class="office-file">
                  {{ file.name }}
                  <MdIcon name="file-word" size="xxxlg" />
                </div>
                <div v-else-if="getFileType(file) === 'vsdx'" class="office-file">
                  {{ file.name }}
                  <MdIcon name="drawing-box" size="xxxlg" />
                </div>
                <Button color="secondary" class="download" @click="dowload(file)">
                  <MdIcon name="download" size="md" />
                </Button>
              </div>
            </div>
          </div>
          <div v-if="message.attachedFileNames && message.attachedFileNames.length" class="attached-file-names">
            <span v-for="file in message.attachedFileNames" :key="file.name || file" class="attached-file-name" :title="file.name || file">
              <img v-if="isImageFile(file)" :src="imageDataUrl(file)" class="attached-file-preview" :alt="file.name" />
              <MdIcon v-else :name="attachedFileIcon(file)" size="lg" />
            </span>
          </div>
          <div v-if="message.templateDataExpanded && message.request" :key="message.request.id" class="template-data">
            <JsonView :value="message.request" />
          </div>
          <div v-if="message.text">
            <span class="body" :class="{ retry: message.retry }" v-html="renderHtmlAsText(message.text)"></span>
          </div>
        </div>
      </div>
      <div ref="scrollToMe"></div>
    </div>
    <div v-else-if="!isRequestPending" class="empty">
      <span>
        Click <Button color="primary" @click="$emit('play')"> <MdIcon title="Continue" name="fast-forward" size="sm"/></Button> to execute prompt
        chain
      </span>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import MdIcon from '@/components/common/MdIcon';
import Button from '@/components/common/Button';
import FormBody from './body/FormBody.vue';
import JsonView from '@/components/common/JsonView';

export default {
  components: {
    MdIcon,
    Button,
    FormBody,
    JsonView
  },
  props: {
    context: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      currentContext: this.sessionContext,
      followScroll: true,
      programmaticScroll: false
    };
  },
  computed: {
    ...mapState({
      sessionContext: s => s.prompts.sample.sessionContext,
      currentRun: s => s.prompts.sample,
      currentlyRunningChain: s => s.prompts.sample.contentChain?.template?.chain ?? [],
      email: s => s.identity.email,
      isRequestPending: s => s.prompts.sample.isRequestPending
    })
  },
  watch: {
    sessionContext: {
      handler(nv) {
        this.currentContext = nv;
      },
      immediate: true
    },
    isRequestPending(nv) {
      if (nv) {
        this.followScroll = true;
      }
    },
    'currentRun.response.length': {
      handler: async function() {
        if (!this.followScroll) {
          return;
        }

        await this.$nextTick();
        this.scrollToBottom();
      }
    },
    'currentRun.response': {
      handler: async function() {
        if (!this.followScroll) {
          return;
        }

        await this.$nextTick();
        this.scrollToBottom();
      },
      deep: true
    }
  },
  methods: {
    renderHtmlAsText(rawHtml) {
      return rawHtml
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\r?\n/g, '<br>');
    },
    getFileType(file) {
      var cleanType = file.type?.split('/')[1] ?? '';

      if (cleanType.includes('wordprocessingml.document')) {
        return 'docx';
      }

      if (cleanType.includes('visio.drawing')) {
        return 'vsdx';
      }

      return cleanType.split('+')[0];
    },
    isImageFile(file) {
      const mimeType = (typeof file === 'object' ? file.mimeType : '') || '';
      return mimeType.startsWith('image/');
    },
    imageDataUrl(file) {
      const { mimeType, content } = file;
      if (!content) return '';
      return content.startsWith('data:') ? content : `data:${mimeType};base64,${content}`;
    },
    attachedFileIcon(file) {
      const mimeType = (typeof file === 'object' ? file.mimeType : '') || '';
      const name = typeof file === 'object' ? file.name : file;
      // Prefer mimeType-based matching
      if (mimeType.startsWith('image/')) return 'file-image-outline';
      if (mimeType === 'application/pdf') return 'file-pdf-box';
      if (mimeType.includes('wordprocessingml') || mimeType.includes('msword')) return 'file-word';
      if (mimeType.includes('presentationml') || mimeType.includes('powerpoint')) return 'file-powerpoint';
      if (mimeType.startsWith('text/plain')) return 'file-document-outline';
      if (mimeType === 'text/markdown') return 'language-markdown';
      if (mimeType === 'message/rfc822') return 'email-outline';
      // Fall back to extension
      const ext = (name.split('.').pop() || '').toLowerCase();
      if (ext === 'pdf') return 'file-pdf-box';
      if (ext === 'docx' || ext === 'doc') return 'file-word';
      if (ext === 'pptx' || ext === 'pptm' || ext === 'ppt') return 'file-powerpoint';
      if (ext === 'txt') return 'file-document-outline';
      if (ext === 'md' || ext === 'markdown') return 'language-markdown';
      if (ext === 'eml') return 'email-outline';
      if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) return 'file-image-outline';
      return 'file-outline';
    },
    dowload(file) {
      const byteCharacters = atob(file.base64.split(';')[1].split(',')[1]);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/octet-stream' });

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = file.name;
      link.click();
      URL.revokeObjectURL(link.href);
    },
    onFormChanged(newValue, form) {
      form.value = newValue.value;
      const link = this.getLinkFromMessage(form);
      link.variables = newValue.variables;
    },
    toggleToll(toolMessage) {
      toolMessage.expanded = !toolMessage.expanded;
    },
    toggleTemplateData(message) {
      message.templateDataExpanded = !message.templateDataExpanded;
    },
    isResultObject(text) {
      const result = this.getResult(text);

      return typeof result === 'object';
    },
    getResult(text) {
      try {
        return JSON.parse(text);
      } catch (error) {
        return text;
      }
    },
    onScroll({ target: { scrollTop, clientHeight, scrollHeight } }) {
      if (this.programmaticScroll) {
        this.resetProgrammaticScrollFlag();
        return;
      }

      if (scrollTop + clientHeight >= scrollHeight - 20) {
        this.followScroll = true;
      } else {
        this.followScroll = false;
      }
    },
    resetProgrammaticScrollFlag() {
      // Use a short delay to reset the flag
      setTimeout(() => {
        this.programmaticScroll = false;
      }, 100);
    },
    linkPrefixText(message) {
      const item = this.getLinkFromMessage(message);

      const indexInGroup = this.currentlyRunningChain.filter(l => l.type === item.type).indexOf(item);
      return `${item.type.substring(0, 1).toUpperCase()}${indexInGroup + 1}`;
    },
    isFormRequiredFieldsProvided(message) {
      const item = this.getLinkFromMessage(message);

      if (!item.variables) {
        return true;
      }

      return item.variables.every(v => {
        if (!v.required) {
          return true;
        }

        if (v.type === 'boolean') {
          return true;
        }

        if (Array.isArray(v.value)) {
          return v.value.length > 0;
        }

        if (Array.isArray(v.sourceFiles) && v.sourceFiles.length > 0) {
          return true;
        }

        return !!v.value;
      });
    },
    getLinkFromMessage(message) {
      return this.currentlyRunningChain.find(l => l.name === message.name && l.type === message.type);
    },
    async next(message) {
      message.done = true;
      await this.$store.dispatch('prompts/sample/continue');
    },
    responseType(m) {
      if (m.type === 'template') {
        return 'code-block-braces';
      }

      if (m.type === 'query') {
        return 'database-search';
      }

      if (m.type === 'tool') {
        return 'function-variant';
      }

      if (m.type === 'image') {
        return 'image';
      }

      return 'robot-confused';
    },
    scrollToBottom() {
      const container = this.$refs.container;
      if (!container) {
        return;
      }

      this.programmaticScroll = true;
      container.scrollTop = container.scrollHeight;
    }
  }
};
</script>

<style scoped lang="scss">
.empty {
  height: 100%;
  width: 100%;
  display: grid;
  align-items: center;
  justify-items: center;
  overflow-y: hidden;

  span {
    font-size: 1rem;
  }
}

.stream-content {
  overflow-y: scroll;
  overflow-x: hidden;
  white-space: pre-line;
  height: 100%;
  font-size: 12px;

  .metadata {
    display: grid;
    gap: 5px;
    font-size: 0.7rem;

    .info {
      display: inline-block;
    }

    .warning {
      color: var(--theme-warning);
      font-size: 0.8rem;
    }
  }

  .form {
    padding: 0.75rem 1rem 0.75rem 1.25rem;
    display: grid;
    grid-template-rows: 1fr max-content;
    gap: 5px;

    .continue-button {
      padding: 3px 9px;
      gap: 5px;
      color: var(--theme-primary);
      font-weight: 600;

      .disabled-icon {
        font-weight: 500;
      }
    }

    .resolving-docs {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 0.8rem;
      color: var(--theme-on-surface);
      opacity: 0.7;
      margin-top: 8px;
      margin-bottom: 8px;

      .spin {
        animation: spin 1s linear infinite;
      }
    }

    label {
      text-transform: uppercase;
      font-weight: 500;
      padding: 2px;
    }

    .prefix {
      color: black;
      background-color: white;
      font-weight: 700;
      font-size: 16;
      border-radius: 2px;
    }

    .form-body {
      background-color: var(--theme-surface);
      padding: 15px;
      border-radius: 3px;

      span {
        user-select: none;
      }

      .variables {
        margin-top: 10px;
        border: 1px solid var(--theme-primary);
        padding: 7px;
        padding-bottom: 0;
        border-radius: 3px;
      }

      span {
        font-size: 14px;
      }
    }
  }

  .toolOrThinking {
    padding: 3px;
    padding-right: 15%;
    padding-left: 1rem;

    .header {
      display: flex;
      align-items: center;
    }

    .label {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      font-size: 0.7rem;
    }

    .button {
      padding: 3px;
      align-items: center;
      align-self: center;
    }

    .body {
      display: none;
      background-color: var(--theme-surface);
      padding: 9px;
      border-bottom-left-radius: 6px;
      border-bottom-right-radius: 6px;
      text-align: left;
      line-height: 1.3rem;
      overflow-wrap: break-word;
      word-wrap: break-word;
      word-break: break-word;

      span {
        font-weight: 700;
      }

      &.retry {
        font-style: italic;
      }

      &.expanded {
        display: inline-block;
      }
    }
  }

  .message {
    padding: 0.75rem 1rem 0.75rem 1.25rem;
    padding-right: 15%;
    $dot-width: 5px;
    $dot-color: var(--theme-primary);
    $speed: 1.5s;

    .typing {
      position: relative;

      span {
        content: '';
        animation: blink $speed infinite;
        animation-fill-mode: both;
        height: $dot-width;
        width: $dot-width;
        background: $dot-color;
        position: absolute;
        left: 0;
        top: 0;
        border-radius: 50%;

        &:nth-child(2) {
          animation-delay: 0.2s;
          margin-left: $dot-width * 1.5;
        }

        &:nth-child(3) {
          animation-delay: 0.4s;
          margin-left: $dot-width * 3;
        }
      }
    }

    .files {
      display: flex;
      justify-content: flex-start;
      flex-wrap: wrap;
      text-align: center;
      padding: 9px;
      border-radius: 6px;
      position: relative;
      gap: 2px;

      .file {
        position: relative;

        .office-file {
          display: grid;
          grid-template-rows: 50px 1fr;
          align-items: center;
          justify-items: center;
          background-color: white;
          padding: 4px;
          color: lightblue;
          border-radius: 2px;
          height: 180px;
          width: 180px;
        }

        .thumbnail {
          z-index: 0;
          padding: 4px;
          background-color: white;
          border-radius: 2px;
          display: grid;
          grid-template-rows: 50px 1fr;
          align-items: center;
          justify-items: center;
          color: lightblue;

          img {
            width: 100%;
            max-width: 100%;
            height: 150px;
            align-self: center;
            justify-self: center;
          }
        }

        .download {
          position: absolute;
          top: 0;
          right: 0;
          color: black;
          padding: 0px;
          align-items: center;
          align-self: center;
        }
      }
    }

    .attached-file-names {
      display: flex;
      flex-wrap: wrap;
      justify-content: flex-end;
      gap: 6px;
      margin-top: 6px;

      .attached-file-name {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 72px;
        height: 72px;
        border-radius: 8px;
        background: var(--theme-surface);
        border: 1px solid var(--theme-on-surface);
        cursor: default;
        overflow: hidden;

        .attached-file-preview {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 5px;
        }
      }
    }

    @keyframes blink {
      0% {
        opacity: 0.1;
      }

      20% {
        opacity: 1;
      }

      100% {
        opacity: 0.1;
      }
    }

    @keyframes spin {
      from {
        transform: rotate(0deg);
      }
      to {
        transform: rotate(360deg);
      }
    }

    &.user {
      text-align: right;
      padding-left: 25%;
      padding-right: 5px;
    }

    .label {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      font-size: 0.7rem;
      font-style: italic;
      color: var(--theme-on-background-accent);
    }

    .template-data-button {
      padding: 1px 3px;
      margin-left: 4px;
      font-style: normal;
    }

    .body {
      display: inline-block;
      background-color: var(--theme-surface);
      padding: 9px;
      border-radius: 6px;
      text-align: left;
      line-height: 1.3rem;

      &.retry {
        font-style: italic;
      }
    }

    .template-data {
      margin-top: 6px;
      border: 1px solid var(--theme-primary);
      border-radius: 6px;
      background-color: var(--theme-surface);
      padding: 9px;
    }

    .message-header {
      display: block;
    }

    .message-skills {
      display: flex;
      flex-wrap: wrap;
      gap: 0.25rem;
      margin-top: 0.25rem;

      .skill-tag {
        background-color: var(--theme-primary);
        color: var(--theme-on-primary, #fff);
        padding: 0.1rem 0.55rem;
        border-radius: 1rem;
        font-size: 0.65rem;
        cursor: default;
        opacity: 0.85;
      }
    }
  }
}
</style>
